import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Shop } from '@shared/components/ui/shop';
import { Avatar, AvatarFallback, AvatarImage } from '@shared/components/ui/shadcn/avatar';
import { faCalendar, faFilter, faPrint, faSearch, faShoppingBag } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePrintOrder } from '../../hooks';
import { Order } from '../../models';
import { OrderDetailsModal } from '../modals';

interface OrdersListProps {
  orders: Order[];
  loading?: boolean;
  error?: string | null;
  sortConfig?: OrderSortConfig;
}

export const OrdersList: React.FC<OrdersListProps> = ({
  orders,
  loading,
  error,
  sortConfig = { field: 'date', direction: 'desc' },
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'prepared' | 'pending'>('all');
  const { printOrder, isOrderLoading } = usePrintOrder();

  // Estados para el modal de detalles del pedido
  const [selectedOrder, setSelectedOrder] = useState<Order | undefined>(undefined);
  const [modalKey, setModalKey] = useState<string>('');
  const orderDetailsModalRef = useRef<ModalCenterRef>(null);

  // Función auxiliar para construir URL completa de imagen (como en Packing original)
  const buildImageUrl = (imageHash: string) => {
    if (!imageHash) return '';
    if (imageHash.startsWith('http')) return imageHash;
    return `https://warehouse-images-stg-dev.nyc3.digitaloceanspaces.com/${imageHash}`;
  };

  // Función para formatear fecha de preparación
  const formatProcessedDate = (processed_at: string | null) => {
    if (!processed_at) return null;

    const date = new Date(processed_at);

    // Formato: dd/mm/yyyy h:m
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  // Función para contar productos totales incluyendo packs
  const getTotalProducts = (order: Order): number => {
    if (!order.lines) return 0;

    return order.lines.reduce((total: number, line: any) => {
      // Si la línea tiene un pack, contar los productos dentro del pack
      if (line.pack && line.pack.lines) {
        const packQuantity = line.qty || 1; // Cantidad de packs
        return (
          total +
          line.pack.lines.reduce((packTotal: number, packLine: any) => {
            // Multiplicar cantidad del producto × cantidad de packs
            return packTotal + (packLine.qty || 1) * packQuantity;
          }, 0)
        );
      }
      // Si es un producto individual, contar su cantidad
      return total + (line.qty || 1);
    }, 0);
  };

  // Filtrar y ordenar pedidos
  const filteredOrders = useMemo(() => {
    let filtered = orders;

    // Filtrar por término de búsqueda (referencia, tracking, nombre cliente)
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (order) =>
          // Buscar por referencia del pedido
          (order.ref?.toLowerCase() || '').includes(searchLower) ||
          // Buscar por nombre del cliente
          (order.customer?.name?.toLowerCase() || '').includes(searchLower) ||
          // Buscar por tracking number
          (order.shipment?.tracking_number?.toLowerCase() || '').includes(searchLower),
      );
    }

    // Filtrar por estado
    if (statusFilter !== 'all') {
      filtered = filtered.filter((order) => {
        const isCompleted = !!order.shipment?.processed_at;
        return statusFilter === 'prepared' ? isCompleted : !isCompleted;
      });
    }

    // Ordenar según el criterio seleccionado
    return filtered.sort((a, b) => {
      // Si el ordenamiento es por fecha
      if (sortConfig.field === 'date') {
        const aCompleted = !!a.shipment?.processed_at;
        const bCompleted = !!b.shipment?.processed_at;

        // Si ambos están completados, ordenar por processed_at
        if (aCompleted && bCompleted) {
          const dateA = new Date(a.shipment!.processed_at!).getTime();
          const dateB = new Date(b.shipment!.processed_at!).getTime();
          const comparison = dateA - dateB;
          return sortConfig.direction === 'asc' ? comparison : -comparison;
        }

        // Si ambos están sin preparar, ordenar por ID
        if (!aCompleted && !bCompleted) {
          const comparison = a.id - b.id;
          return sortConfig.direction === 'asc' ? comparison : -comparison;
        }

        // Si uno está completado y otro no, priorizar según la dirección
        if (sortConfig.direction === 'desc') {
          // desc: completados primero
          return aCompleted ? -1 : 1;
        } else {
          // asc: pendientes primero
          return aCompleted ? 1 : -1;
        }
      }

      // Ordenar por referencia
      if (sortConfig.field === 'reference') {
        const comparison = (a.ref || '').localeCompare(b.ref || '');
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      // Ordenar por nombre del cliente
      if (sortConfig.field === 'customer') {
        const comparison = (a.customer?.name || '').localeCompare(b.customer?.name || '');
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      return 0;
    });
  }, [orders, searchTerm, statusFilter, sortConfig]);

  // useEffect para abrir modal cuando selectedOrder o modalKey cambien
  useEffect(() => {
    if (selectedOrder && modalKey) {
      orderDetailsModalRef.current?.openModal();
    }
  }, [selectedOrder, modalKey]);

  const handleOrderClick = (order: Order) => {
    // Generar una clave única para forzar la reapertura del modal
    const newKey = `${order.id}-${Date.now()}`;
    setModalKey(newKey);
    setSelectedOrder(order);
  };

  const handlePrintOrder = async (order: Order, e: React.MouseEvent) => {
    e.stopPropagation();
    await printOrder(order.id);
  };

  if (loading) {
    return (
      <div className="bg-background border border-border rounded-lg p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground text-sm">Cargando pedidos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-background border border-border rounded-lg p-6">
        <div className="text-center">
          <div className="text-destructive mb-2">
            <svg className="mx-auto h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <p className="text-destructive text-sm font-medium">Error al cargar pedidos</p>
          <p className="text-muted-foreground text-xs mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background border border-border rounded-lg flex flex-col mb-4">
      {/* Search and Filters Header */}
      <div className="p-4 border-b border-border flex-shrink-0">
        {/* Search Bar with Status Filter */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4"
            />
            <Input
              type="text"
              placeholder="Buscar por referencia, tracking o cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Status Filter - Same height as search */}
          <Select
            value={statusFilter}
            onValueChange={(value: 'all' | 'prepared' | 'pending') => setStatusFilter(value)}
          >
            <SelectTrigger className="w-[140px] h-10 text-sm border-0 bg-muted/50 hover:bg-muted">
              <FontAwesomeIcon icon={faFilter} className="w-3 h-3 mr-1 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pending">Pendientes</SelectItem>
              <SelectItem value="prepared">Preparados</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Orders List */}
      <div className="flex-1">
        <div className="space-y-3 p-4">
          {filteredOrders.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground text-sm">
                {searchTerm ? 'No se encontraron pedidos con ese criterio de búsqueda' : 'No hay pedidos disponibles'}
              </p>
            </div>
          ) : (
            filteredOrders.map((order, index) => (
              <div
                key={order.id}
                className={`py-3 ${index < filteredOrders.length - 1 ? 'border-b border-border' : ''}`}
                data-testid={`order-${order.id}`}
              >
                {/* Order Card */}
                <div className="space-y-2">
                  {/* Order Header */}
                  <div className="flex items-center justify-between">
                    {/* Shop Info */}
                    <div
                      className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => handleOrderClick(order)}
                      data-testid={`shop-info-${order.id}`}
                    >
                      {/* Shop Avatar */}
                      <Avatar className="w-8 h-8 border border-border">
                        <AvatarImage src={buildImageUrl(order.shop?.image_hash || '')} alt={order.shop?.name || ''} />
                        <AvatarFallback className="bg-muted text-xs font-medium">
                          {(order.shop?.name || '').slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      {/* Shop Details */}
                      <div>
                        <div className="text-sm font-medium text-foreground">{order.ref || ''}</div>
                        <div className="text-xs text-muted-foreground">{order.shop?.name || 'Sin tienda'}</div>
                      </div>
                    </div>

                    {/* Order Actions */}
                    <div className="flex items-center gap-3">
                      {/* Quantity Display */}
                      <div
                        className="flex items-center gap-2 cursor-pointer hover:opacity-80"
                        onClick={() => handleOrderClick(order)}
                        data-testid={`order-quantity-${order.id}`}
                      >
                        <FontAwesomeIcon icon={faShoppingBag} className="w-3 h-3 text-muted-foreground" />
                        <span className="text-xs text-foreground">{getTotalProducts(order)}</span>
                      </div>

                      {/* Print Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        className="py-2 px-3"
                        data-testid={`print-button-${order.id}`}
                        onClick={(e) => handlePrintOrder(order, e)}
                        disabled={isOrderLoading(order.id)}
                      >
                        {isOrderLoading(order.id) ? (
                          <div className="w-3 h-3 border border-muted-foreground border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <FontAwesomeIcon icon={faPrint} className="w-3 h-3 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Order Footer */}
                  <div className="flex items-center gap-2 justify-between">
                    {/* Status Badge */}
                    <Badge
                      variant="outline"
                      className={`text-xs border-0 flex items-center gap-2 ${
                        order.shipment?.processed_at
                          ? 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300'
                          : 'bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-300'
                      }`}
                    >
                      <FontAwesomeIcon icon={faCalendar} className="w-3 h-3" />
                      {order.shipment?.processed_at
                        ? `${formatProcessedDate(order.shipment.processed_at) || ''}`
                        : 'Por preparar'}
                    </Badge>

                    {/* Courier Component */}
                    {order.shipment?.courier && (
                      <Shop
                        src={buildImageUrl(order.shipment.courier.image_hash || '')}
                        code={order.shipment.tracking_number || ''}
                      />
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal de detalles del pedido */}
      <OrderDetailsModal key={modalKey} ref={orderDetailsModalRef} order={selectedOrder} />
    </div>
  );
};

// Tipos para el ordenamiento de pedidos (igual que en Picking)
export type OrderSortField = 'date' | 'reference' | 'customer';
export type OrderSortDirection = 'asc' | 'desc';

export interface OrderSortConfig {
  field: OrderSortField;
  direction: OrderSortDirection;
}

type OrderSortOption = {
  value: string;
  label: string;
  field: OrderSortField;
  direction: OrderSortDirection;
};

// Componente para controles de ordenamiento (igual que en Picking)
interface OrderSortControlsProps {
  sortConfig: OrderSortConfig;
  onSortChange: (config: OrderSortConfig) => void;
}

export const OrderSortControls: React.FC<OrderSortControlsProps> = ({ sortConfig, onSortChange }) => {
  const sortOptions: OrderSortOption[] = [
    {
      value: 'date-asc',
      label: 'Fecha de más antiguo a más reciente',
      field: 'date',
      direction: 'asc',
    },
    {
      value: 'date-desc',
      label: 'Fecha de más reciente a más antiguo',
      field: 'date',
      direction: 'desc',
    },
    {
      value: 'reference-asc',
      label: 'Referencia de A a Z',
      field: 'reference',
      direction: 'asc',
    },
    {
      value: 'reference-desc',
      label: 'Referencia de Z a A',
      field: 'reference',
      direction: 'desc',
    },
    {
      value: 'customer-asc',
      label: 'Cliente de A a Z',
      field: 'customer',
      direction: 'asc',
    },
    {
      value: 'customer-desc',
      label: 'Cliente de Z a A',
      field: 'customer',
      direction: 'desc',
    },
  ];

  // Construir el valor actual basado en el sortConfig
  const currentValue = `${sortConfig.field}-${sortConfig.direction}`;

  const handleSortChange = (value: string) => {
    const selectedOption = sortOptions.find((option) => option.value === value);
    if (selectedOption) {
      onSortChange({
        field: selectedOption.field,
        direction: selectedOption.direction,
      });
    }
  };

  // Obtener la etiqueta del valor actual para mostrar en el trigger
  const currentLabel = sortOptions.find((option) => option.value === currentValue)?.label || 'Seleccionar ordenación';

  return (
    <Select value={currentValue} onValueChange={handleSortChange}>
      <SelectTrigger className="w-[280px]">
        <SelectValue placeholder="Seleccionar ordenación">{currentLabel}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {sortOptions.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
